import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

import {
    Add,
    Delete,
    Edit,
    Visibility,
    Print,
    Refresh,
    Search,
} from "@mui/icons-material";

import { useNavigate } from "react-router-dom";
import AuthService from "../../Auth/AuthService";

const QuestionPaperList = ({ user }) => {

    const navigate = useNavigate();
    const [papers, setPapers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [searchText, setSearchText] = useState("");
    const [subjectFilter, setSubjectFilter] = useState("");
    const [classFilter, setClassFilter] = useState("");
    const [examFilter, setExamFilter] = useState("");
    const [yearFilter, setYearFilter] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const hasFetched = useRef(false);

    const loadQuestionPapers = async () => {

        try {
            setLoading(true);
            setError("");
            const response = await AuthService.getAuth(AuthService.pathUrl("GET", "/question-papers"));
            setPapers(Array.isArray(response) ? response : []);
        } catch (err) {
            console.error("Error loading question papers:", err);
            setError(err.response?.message || "Unable to load question papers.");
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;
        loadQuestionPapers();
    }, []);

    const subjects = useMemo(() => {
        return [
            ...new Set(
                papers
                    .map((paper) => paper.subject)
                    .filter(Boolean)
            ),
        ].sort();

    }, [papers]);


    const classes = useMemo(() => {
        return [
            ...new Set(
                papers
                    .map((paper) => paper.className)
                    .filter(Boolean)
            ),
        ].sort();

    }, [papers]);


    const exams = useMemo(() => {
        return [
            ...new Set(
                papers
                    .map((paper) => paper.examName)
                    .filter(Boolean)
            ),
        ].sort();

    }, [papers]);


    const academicYears = useMemo(() => {
        return [
            ...new Set(
                papers
                    .map((paper) => paper.academicYear)
                    .filter(Boolean)
            ),
        ].sort();

    }, [papers]);


    // =========================================================
    // FILTER
    // =========================================================

    const filteredPapers = useMemo(() => {

        const search = searchText
            .trim()
            .toLowerCase();

        return papers.filter((paper) => {

            const matchesSearch =
                !search ||
                String(paper.title || "")
                    .toLowerCase()
                    .includes(search) ||
                String(paper.subject || "")
                    .toLowerCase()
                    .includes(search) ||
                String(paper.examName || "")
                    .toLowerCase()
                    .includes(search) ||
                String(paper.className || "")
                    .toLowerCase()
                    .includes(search);

            const matchesSubject =
                !subjectFilter ||
                paper.subject === subjectFilter;

            const matchesClass =
                !classFilter ||
                paper.className === classFilter;

            const matchesExam =
                !examFilter ||
                paper.examName === examFilter;

            const matchesYear =
                !yearFilter ||
                paper.academicYear === yearFilter;

            return (
                matchesSearch &&
                matchesSubject &&
                matchesClass &&
                matchesExam &&
                matchesYear
            );

        });

    }, [
        papers,
        searchText,
        subjectFilter,
        classFilter,
        examFilter,
        yearFilter,
    ]);

    const handleDelete = async (id) => {

        const confirmed = window.confirm("Are you sure you want to delete this question paper?");

        if (!confirmed) {
            return;
        }

        try {

            await AuthService.deleteAuth(AuthService.pathUrl("DELETE", `/question-papers/${id}`));
            setPapers((previous) =>
                previous.filter((paper) => paper.id !== id)
            );

        } catch (err) {
            console.error("Delete question paper error:", err);
            alert(err.response?.data?.message || "Unable to delete question paper.");
        }
    };

    const handleStatus = async (id) => {
        if (user?.roles === "ADMIN" || user?.roles === "SUPERADMIN") {
            try {
                const res = await AuthService.putAuth(AuthService.pathUrl("PUT", `/question-papers/${id}/status`));
                setPapers((previous) =>
                    previous.map((paper) =>
                        paper.id === res.id
                            ? { ...paper, status: res.status }
                            : paper
                    )
                );
            } catch (err) {
                console.error("Delete question paper error:", err);
                alert(err.response?.data?.message || "Unable to delete question paper.");
            }
        }
    };

    // =========================================================
    // RESET FILTERS
    // =========================================================

    const resetFilters = () => {
        setSearchText("");
        setSubjectFilter("");
        setClassFilter("");
        setExamFilter("");
        setYearFilter("");
        setPage(0);
    };

    const paginatedPapers = filteredPapers.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handlePrint = (id) => {
        navigate(`/question-papers/${id}?print=true`);
    };


    return (
        <Box
            sx={{
                p: { xs: 1, sm: 2, md: 3 },
            }}
        >

            {/* ================================================= */}
            {/* HEADER */}
            {/* ================================================= */}

            <Stack
                direction={{
                    xs: "column",
                    sm: "row",
                }}
                justifyContent="space-between"
                alignItems={{
                    xs: "stretch",
                    sm: "center",
                }}
                spacing={2}
                sx={{ mb: 2 }}
            >

                <Box>

                    <Typography
                        variant="h5"
                        fontWeight={600}
                    >
                        Question Papers
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        View and manage all created question papers
                    </Typography>

                </Box>


                <Stack
                    direction="row"
                    spacing={1}
                >

                    <Tooltip title="Refresh">

                        <IconButton
                            onClick={loadQuestionPapers}
                            disabled={loading}
                        >
                            <Refresh />
                        </IconButton>

                    </Tooltip>


                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() =>
                            navigate("/question-papers/create")
                        }
                    >
                        Create Question Paper
                    </Button>

                </Stack>

            </Stack>


            {/* ================================================= */}
            {/* FILTER CARD */}
            {/* ================================================= */}

            <Card
                sx={{
                    mb: 2,
                    borderRadius: 2,
                }}
            >

                <CardContent>

                    <Grid
                        container
                        spacing={2}
                    >

                        {/* Search */}

                        <Grid
                            size={{
                                xs: 12,
                                sm: 6,
                                md: 3,
                            }}
                        >

                            <TextField
                                fullWidth
                                size="small"
                                label="Search"
                                placeholder="Title, subject, exam..."
                                value={searchText}
                                onChange={(event) => {
                                    setSearchText(
                                        event.target.value
                                    );
                                    setPage(0);
                                }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <Search
                                                sx={{
                                                    mr: 1,
                                                    color: "text.secondary",
                                                }}
                                            />
                                        ),
                                    }
                                }}
                            />

                        </Grid>


                        {/* Subject */}

                        <Grid
                            size={{
                                xs: 12,
                                sm: 6,
                                md: 2,
                            }}
                        >

                            <FormControl
                                fullWidth
                                size="small"
                            >

                                <InputLabel>
                                    Subject
                                </InputLabel>

                                <Select
                                    value={subjectFilter}
                                    label="Subject"
                                    onChange={(event) => {
                                        setSubjectFilter(
                                            event.target.value
                                        );
                                        setPage(0);
                                    }}
                                >

                                    <MenuItem value="">
                                        All Subjects
                                    </MenuItem>

                                    {subjects.map(
                                        (subject) => (
                                            <MenuItem
                                                key={subject}
                                                value={subject}
                                            >
                                                {subject}
                                            </MenuItem>
                                        )
                                    )}

                                </Select>

                            </FormControl>

                        </Grid>


                        {/* Class */}

                        <Grid
                            size={{
                                xs: 12,
                                sm: 6,
                                md: 2,
                            }}
                        >

                            <FormControl
                                fullWidth
                                size="small"
                            >

                                <InputLabel>
                                    Class
                                </InputLabel>

                                <Select
                                    value={classFilter}
                                    label="Class"
                                    onChange={(event) => {
                                        setClassFilter(
                                            event.target.value
                                        );
                                        setPage(0);
                                    }}
                                >

                                    <MenuItem value="">
                                        All Classes
                                    </MenuItem>

                                    {classes.map(
                                        (className) => (
                                            <MenuItem
                                                key={className}
                                                value={className}
                                            >
                                                {className}
                                            </MenuItem>
                                        )
                                    )}

                                </Select>

                            </FormControl>

                        </Grid>


                        {/* Exam */}

                        <Grid
                            size={{
                                xs: 12,
                                sm: 6,
                                md: 2,
                            }}
                        >

                            <FormControl
                                fullWidth
                                size="small"
                            >

                                <InputLabel>
                                    Exam
                                </InputLabel>

                                <Select
                                    value={examFilter}
                                    label="Exam"
                                    onChange={(event) => {
                                        setExamFilter(
                                            event.target.value
                                        );
                                        setPage(0);
                                    }}
                                >

                                    <MenuItem value="">
                                        All Exams
                                    </MenuItem>

                                    {exams.map(
                                        (exam) => (
                                            <MenuItem
                                                key={exam}
                                                value={exam}
                                            >
                                                {exam}
                                            </MenuItem>
                                        )
                                    )}

                                </Select>

                            </FormControl>

                        </Grid>


                        {/* Academic Year */}

                        <Grid
                            size={{
                                xs: 12,
                                sm: 6,
                                md: 2,
                            }}
                        >

                            <FormControl
                                fullWidth
                                size="small"
                            >

                                <InputLabel>
                                    Academic Year
                                </InputLabel>

                                <Select
                                    value={yearFilter}
                                    label="Academic Year"
                                    onChange={(event) => {
                                        setYearFilter(
                                            event.target.value
                                        );
                                        setPage(0);
                                    }}
                                >

                                    <MenuItem value="">
                                        All Years
                                    </MenuItem>

                                    {academicYears.map(
                                        (year) => (
                                            <MenuItem
                                                key={year}
                                                value={year}
                                            >
                                                {year}
                                            </MenuItem>
                                        )
                                    )}

                                </Select>

                            </FormControl>

                        </Grid>


                        {/* Reset */}

                        <Grid
                            size={{
                                xs: 12,
                                md: 1,
                            }}
                        >

                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={resetFilters}
                                sx={{
                                    height: 40,
                                }}
                            >
                                Reset
                            </Button>

                        </Grid>

                    </Grid>

                </CardContent>

            </Card>


            {/* ================================================= */}
            {/* ERROR */}
            {/* ================================================= */}

            {error && (

                <Card
                    sx={{
                        mb: 2,
                        p: 2,
                    }}
                >

                    <Typography
                        color="error"
                    >
                        {error}
                    </Typography>

                </Card>

            )}


            {/* ================================================= */}
            {/* TABLE */}
            {/* ================================================= */}

            <Card
                sx={{
                    borderRadius: 2,
                }}
            >

                <TableContainer>

                    <Table>

                        <TableHead>

                            <TableRow>

                                <TableCell>
                                    #
                                </TableCell>

                                <TableCell>
                                    Title
                                </TableCell>

                                <TableCell>
                                    Subject
                                </TableCell>

                                <TableCell>
                                    Class
                                </TableCell>

                                <TableCell>
                                    Exam
                                </TableCell>

                                <TableCell>
                                    Academic Year
                                </TableCell>

                                <TableCell align="center">
                                    Questions
                                </TableCell>

                                <TableCell align="center">
                                    Marks
                                </TableCell>

                                <TableCell>
                                    Status
                                </TableCell>

                                <TableCell align="center">
                                    Action
                                </TableCell>

                            </TableRow>

                        </TableHead>


                        <TableBody>

                            {loading ? (

                                <TableRow>

                                    <TableCell
                                        colSpan={10}
                                        align="center"
                                        sx={{ py: 6 }}
                                    >

                                        <CircularProgress />

                                    </TableCell>

                                </TableRow>

                            ) : paginatedPapers.length === 0 ? (

                                <TableRow>

                                    <TableCell
                                        colSpan={10}
                                        align="center"
                                        sx={{ py: 6 }}
                                    >

                                        <Typography
                                            color="text.secondary"
                                        >
                                            No question papers found.
                                        </Typography>

                                    </TableCell>

                                </TableRow>

                            ) : (

                                paginatedPapers.map(
                                    (paper, index) => (

                                        <TableRow
                                            key={paper.id}
                                            hover
                                        >

                                            <TableCell>
                                                {page *
                                                    rowsPerPage +
                                                    index +
                                                    1}
                                            </TableCell>


                                            <TableCell>


                                                {paper.title}


                                            </TableCell>


                                            <TableCell>
                                                {paper.subject ||
                                                    "-"}
                                            </TableCell>


                                            <TableCell>
                                                {paper.className ||
                                                    "-"}
                                            </TableCell>


                                            <TableCell>
                                                {paper.examName ||
                                                    "-"}
                                            </TableCell>


                                            <TableCell>
                                                {paper.academicYear ||
                                                    "-"}
                                            </TableCell>


                                            <TableCell align="center">
                                                {paper.questionCount ??
                                                    0}
                                            </TableCell>


                                            <TableCell align="center">
                                                {paper.totalMarks ??
                                                    0}
                                            </TableCell>


                                            <TableCell>

                                                <Chip
                                                    size="small"
                                                    label={
                                                        paper.status ||
                                                        "DRAFT"
                                                    }
                                                    color={
                                                        paper.status ===
                                                            "PUBLISHED"
                                                            ? "success"
                                                            : "default"
                                                    }
                                                    onClick={() => handleStatus(paper.id)}
                                                />

                                            </TableCell>


                                            <TableCell>

                                                <Stack
                                                    direction="row"
                                                    justifyContent="center"
                                                    spacing={0.5}
                                                >

                                                    <Tooltip title="View">

                                                        <IconButton
                                                            color="primary"
                                                            onClick={() => navigate(`/question-papers/${paper.id}`)}
                                                        >
                                                            <Visibility />
                                                        </IconButton>

                                                    </Tooltip>


                                                    <Tooltip title="Edit">

                                                        <IconButton
                                                            color="secondary"
                                                            onClick={() => navigate(`/question-papers/${paper.id}/edit`)}
                                                        >
                                                            <Edit />
                                                        </IconButton>

                                                    </Tooltip>


                                                    <Tooltip title="Print">

                                                        <IconButton
                                                            color="success"
                                                            onClick={() => handlePrint(paper.id)}
                                                        >
                                                            <Print />
                                                        </IconButton>

                                                    </Tooltip>


                                                    <Tooltip title="Delete">

                                                        <IconButton
                                                            color="error"
                                                            onClick={() => handleDelete(paper.id)}
                                                        >
                                                            <Delete />
                                                        </IconButton>

                                                    </Tooltip>

                                                </Stack>

                                            </TableCell>

                                        </TableRow>

                                    )
                                )

                            )}

                        </TableBody>

                    </Table>

                </TableContainer>


                <Divider />


                <TablePagination
                    component="div"
                    count={filteredPapers.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={
                        handleChangeRowsPerPage
                    }
                    rowsPerPageOptions={[
                        5,
                        10,
                        25,
                        50,
                    ]}
                />

            </Card>

        </Box>
    );
};

export default QuestionPaperList;